import type { RefObject } from 'react'

import { useEffect, useRef } from 'react'

const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
].join(',')

// Keeps keyboard focus inside an open dialog, closes it on Escape and
// returns focus to whatever opened it once it closes.
export const useFocusTrap = (ref: RefObject<HTMLElement | null>, active: boolean, onEscape?: () => void): void => {
    const escapeRef = useRef(onEscape)

    useEffect(() => {
        escapeRef.current = onEscape
    }, [onEscape])

    useEffect(() => {
        if (!active) return

        const container = ref.current

        if (!container) return

        const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
        const focusables = (): Array<HTMLElement> => Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(element => element.getClientRects().length > 0)

        const frame = window.requestAnimationFrame(() => {
            const initial = container.querySelector<HTMLElement>('[data-autofocus]') ?? focusables()[0] ?? container

            initial.focus({ preventScroll: true })
        })

        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') {
                event.stopPropagation()
                escapeRef.current?.()

                return
            }

            if (event.key !== 'Tab') return

            const items = focusables()

            if (items.length === 0) {
                event.preventDefault()

                return
            }

            const first = items[0]
            const last = items[items.length - 1]

            if (event.shiftKey && (document.activeElement === first || document.activeElement === container)) {
                event.preventDefault()
                last.focus()
            }
            else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault()
                first.focus()
            }
        }

        container.addEventListener('keydown', onKeyDown)

        return () => {
            window.cancelAnimationFrame(frame)
            container.removeEventListener('keydown', onKeyDown)

            if (opener && document.contains(opener)) opener.focus({ preventScroll: true })
        }
    }, [ref, active])
}
